'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { Box, Typography } from '@mui/material'
import { Toaster } from 'react-hot-toast'

import { useWallet, useWalletContentData } from '@/src/hooks/wallet'
import { getMobileDeviceType } from '@/src/utils/deviceType'
import useRedirectToHome from '@/src/hooks/navigation/useRedirectToHome'
import useRedirectFromWallet from '@/src/hooks/navigation/useRedirectFromWallet'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer/swipeableDrawer'
import { MultiBrandWalletInfo, WalletTransactionsResponse } from '@/src/types/wallet'
import { WalletFAQCollection } from '@/src/types/walletFaq'

import WalletMobileFlyinContent from './WalletMobileFlyinContent'
import WalletMobileCleartripContent from './WalletMobileCleartripContent'
import { WalletPageShimmer } from './walletShimmer'
import { WalletFAQ } from './walletFAQ'
import BottomToolbarWrapper from '../bottomToolbar'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'

interface WalletPageWrapperProps {
    isAppWallet?: boolean
    initialWalletInfo?: MultiBrandWalletInfo | null
    initialTransactions?: WalletTransactionsResponse | null
    initialFAQs?: WalletFAQCollection | null
    showVisa?: boolean
    showFlightsHotels?: boolean
    showHolidays?: boolean
    isMultiBrand?: boolean
}

// Dynamically load SignInModal to reduce initial bundle size
const SignInModalWrapper = dynamic(
    () =>
        import(
            /* webpackChunkName: "signin-modal" */ '@/src/components/common/header/variants/flyin/SignInModal/SignInModalWrapper'
        ),
    { ssr: false }
)

const WalletPageWrapper: React.FC<WalletPageWrapperProps> = ({
    isAppWallet = false,
    initialWalletInfo,
    initialTransactions,
    initialFAQs,
    showVisa,
    showFlightsHotels,
    showHolidays,
    isMultiBrand: isMultiBrandSsr = false
}) => {
    const isMultiBrand = isMultiBrandSsr

    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('wallet')
    const tCommon = useTranslations('common')
    const aboutWalletLabel = t('aboutWalletGeneric')
    const isRtl = locale === 'ar'

    const [modalOpen, setModalOpen] = useState(false)
    const [faqDrawerOpen, setFaqDrawerOpen] = useState(false)

    // Use wallet hook with initial SSR data
    const wallet = useWallet({
        initialWalletInfo,
        initialTransactions,
        isMultiBrand
    })
    const goToHome = useRedirectToHome()
    const goBackFromWallet = useRedirectFromWallet()

    // Check if device is Android or iOS app (client-side only)
    const [isNativeApp, setIsNativeApp] = useState(false)
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const mobileDeviceType = getMobileDeviceType()
            setIsNativeApp(mobileDeviceType === 'AndroidApp' || mobileDeviceType === 'iPhoneApp')
        }
    }, [])

    // Redirect to sign-in if not logged in
    useEffect(() => {
        if (typeof window !== 'undefined' && !wallet.isLoggedIn) {
            setModalOpen(true)
        }
    }, [wallet.isLoggedIn])

    // Handlers
    const handleModalClose = useCallback(() => {
        setModalOpen(false)
        // If still not logged in, redirect to account page
        if (!wallet.isLoggedIn) {
            router.push(`/${locale}/account`)
        }
    }, [wallet.isLoggedIn, router, locale])

    const handleOpenFAQDrawer = useCallback(() => {
        setFaqDrawerOpen(true)
    }, [])

    const handleCloseFAQDrawer = useCallback(() => {
        setFaqDrawerOpen(false)
    }, [])

    const {
        formattedTransactions,
        cleartripFormattedTransactions,
        translatedBalanceError,
        translatedTransactionsError,
        formatAmount,
        cleartripTransactionLabels,
        brandFilter,
        setBrandFilter,
        brandBalances
    } = useWalletContentData({ wallet, locale })

    const handleGoToHome = useCallback(() => {
        // If appWallet is true, use wallet callback to go back to app
        if (isAppWallet && isCleartrip()) {
            goToHome({ isReloadRequired: false })
        } else if (isAppWallet || isNativeApp) {
            goBackFromWallet({ isReloadRequired: false })
        } else {
            goToHome({ isReloadRequired: false })
        }
    }, [goToHome, goBackFromWallet, isAppWallet, isNativeApp])

    // Determine if we should show full page shimmer
    // Only show shimmer if we don't have initial data and are loading
    const hasInitialData = !!(initialWalletInfo || initialTransactions)
    const showFullPageShimmer = !hasInitialData && wallet.isLoadingBalance && wallet.isLoadingTransactions

    const baseWalletPageProps = useMemo(
        () => ({
            displayBalance: wallet.displayBalance,
            displayCurrency: wallet.displayCurrency,
            domainBalances: wallet.domainBalances,
            hasMultipleDomainBalances: wallet.hasMultipleDomainBalances,
            brandBalances,
            hasMoreTransactions: wallet.hasMoreTransactions,
            activeTab: wallet.activeTab,
            onTabChange: wallet.setActiveTab,
            brandFilter,
            onBrandFilterChange: setBrandFilter,
            isLoadingBalance: wallet.isLoadingBalance,
            isFetchingMore: wallet.isFetchingTransactions,
            balanceError: translatedBalanceError,
            transactionsError: translatedTransactionsError,
            onLoadMore: wallet.loadMoreTransactions,
            onRefresh: wallet.refreshWallet,
            onGoToHome: handleGoToHome,
            showBackButton: true,
            isFAQDrawerOpen: faqDrawerOpen,
            onOpenFAQDrawer: handleOpenFAQDrawer,
            onCloseFAQDrawer: handleCloseFAQDrawer,
            formatAmount,
            t,
            tCommon,
            locale,
            aboutWalletLabel,
            showMultiBrandWallet: isMultiBrand
        }),
        [
            wallet.displayBalance,
            wallet.displayCurrency,
            wallet.domainBalances,
            wallet.hasMultipleDomainBalances,
            brandBalances,
            wallet.hasMoreTransactions,
            wallet.activeTab,
            wallet.setActiveTab,
            brandFilter,
            setBrandFilter,
            wallet.isLoadingBalance,
            wallet.isFetchingTransactions,
            translatedBalanceError,
            translatedTransactionsError,
            wallet.loadMoreTransactions,
            wallet.refreshWallet,
            handleGoToHome,
            faqDrawerOpen,
            handleOpenFAQDrawer,
            handleCloseFAQDrawer,
            formatAmount,
            t,
            tCommon,
            locale,
            aboutWalletLabel,
            isMultiBrand
        ]
    )

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'grey.100'
            }}
        >
            <Toaster position="bottom-center" reverseOrder={false} gutter={8} />
            {/* Main Content */}
            <Box sx={{ flex: 1 }}>
                {showFullPageShimmer ? (
                    <WalletPageShimmer showBackButton={true} />
                ) : (
                    <>
                        {wallet.isCleartrip ? (
                            <WalletMobileCleartripContent
                                walletPageProps={{
                                    ...baseWalletPageProps,
                                    formattedTransactions: [],
                                    isLoadingTransactions: false
                                }}
                                transactions={cleartripFormattedTransactions}
                                hasMoreTransactions={wallet.hasMoreTransactions}
                                isFetchingMore={wallet.isFetchingTransactions}
                                onLoadMore={wallet.loadMoreTransactions}
                                formatAmount={formatAmount}
                                {...cleartripTransactionLabels}
                                isRtl={isRtl}
                            />
                        ) : (
                            <WalletMobileFlyinContent
                                {...baseWalletPageProps}
                                formattedTransactions={formattedTransactions}
                                cleartripFallbackTransactions={cleartripFormattedTransactions}
                                cleartripTransactionLabels={cleartripTransactionLabels}
                                isRtl={isRtl}
                                isLoadingTransactions={wallet.isLoadingTransactions}
                            />
                        )}
                    </>
                )}

                {/* Hide bottom toolbar when appWallet is true */}
                {!isAppWallet && <BottomToolbarWrapper showVisa={showVisa} showFlightsHotels={showFlightsHotels} showHolidays={showHolidays} />}
            </Box>

            {/* Sign In Modal */}
            {modalOpen && <SignInModalWrapper open={modalOpen} onClose={handleModalClose} />}

            {/* FAQ Drawer */}
            <SwipeableDrawerWithBackNavigation
                open={faqDrawerOpen}
                onClose={handleCloseFAQDrawer}
                sx={{ height: 'auto', maxHeight: '90dvh' }}
            >
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <WalletFAQ initialFAQs={initialFAQs} isCleartripBrand={wallet.isCleartrip} isMultiBrand={isMultiBrand} />
                </Box>
            </SwipeableDrawerWithBackNavigation>
        </Box>
    )
}

export { WalletPageWrapper }
export default WalletPageWrapper
